<template>
  <div
    :id="id"
    ref="contextMenu"
    class="ctx-menu-container"
    :style="ctxStyle"
    @click.stop
    @contextmenu.stop
  >
    <div
      class="ctx open"
      style="background-color: transparent"
    >
      <ul
        role="menu"
        class="ctx-menu"
        :class="{
          'ctx-menu-right': align === 'right',
          'ctx-menu-left': align === 'left',
        }"
      >
        <slot />
      </ul>
    </div>
  </div>
</template>

<script>
// Vue 3 port of `vue-context-menu` (a Vue 2-only lib whose compiled render used
// `this._c` and crashes under Vue 3). Same public API: `open(e, data)` where `e`
// carries clientX/clientY (or pageX/pageY), a default slot for the items, and the
// `ctx-open`/`ctx-close`/`ctx-cancel` events. Closes on an outside click or Esc.
export default {
  name: 'AppContextMenu',
  props: {
    id: {
      type: String,
      default: 'default-ctx',
    },
    align: {
      type: String,
      default: 'left',
    },
  },
  emits: ['ctx-open', 'ctx-close', 'ctx-cancel'],
  data() {
    return {
      locals: {},
      ctxTop: 0,
      ctxLeft: 0,
      ctxVisible: false,
    };
  },
  computed: {
    ctxStyle() {
      return {
        display: this.ctxVisible ? 'block' : 'none',
        top: (this.ctxTop || 0) + 'px',
        left: (this.ctxLeft || 0) + 'px',
      };
    },
  },
  beforeUnmount() {
    this.stopListening();
  },
  methods: {
    open(e, data) {
      this.ctxVisible = true;
      this.$emit('ctx-open', (this.locals = data || {}));
      this.setPositionFromEvent(e);
      this.startListening();

      return this;
    },
    close() {
      this.ctxVisible = false;
      this.stopListening();
    },
    // Cross-browser positioning, ported from the original lib.
    setPositionFromEvent(e) {
      e = e || window.event;

      const scrollingElement = document.scrollingElement || document.documentElement;

      if (e.pageX || e.pageY) {
        this.ctxLeft = e.pageX;
        this.ctxTop = e.pageY - scrollingElement.scrollTop;
      } else if (e.clientX || e.clientY) {
        this.ctxLeft = e.clientX + scrollingElement.scrollLeft;
        this.ctxTop = e.clientY + scrollingElement.scrollTop;
      }

      this.$nextTick(() => {
        const menu = this.$el;
        const minHeight = (menu.style.minHeight || menu.style.height).replace('px', '') || 32;
        const minWidth = (menu.style.minWidth || menu.style.width).replace('px', '') || 32;
        const scrollHeight = menu.scrollHeight || minHeight;
        const scrollWidth = menu.scrollWidth || minWidth;
        const largestHeight = window.innerHeight - scrollHeight - 25;
        const largestWidth = window.innerWidth - scrollWidth - 25;

        if (this.ctxTop > largestHeight) {this.ctxTop = largestHeight;}
        if (this.ctxLeft > largestWidth) {this.ctxLeft = largestWidth;}
      });
    },
    // Capture phase so we can intercept the click before other handlers, matching
    // the original `body-click-listener` (useCapture = true).
    startListening() {
      window.addEventListener('click', this.onBodyClick, true);
      window.addEventListener('keyup', this.onBodyKeyup, true);
    },
    stopListening() {
      window.removeEventListener('click', this.onBodyClick, true);
      window.removeEventListener('keyup', this.onBodyKeyup, true);
    },
    onBodyClick(e) {
      e.preventDefault();

      const outsideClick = this.ctxVisible && !this.$el.contains(e.target);

      if (outsideClick) {
        // Ignore non-primary buttons without closing.
        if (e.which !== 1) {
          e.stopPropagation();

          return;
        }

        this.close();
        this.$emit('ctx-cancel', this.locals);
        e.stopPropagation();
      } else {
        // Inside click: close but let the event reach the item handler.
        this.close();
        this.$emit('ctx-close', this.locals);
      }
    },
    onBodyKeyup(e) {
      if (e.keyCode === 27) {this.onBodyClick(e);}
    },
  },
};
</script>

<style>
/* Ported verbatim from vue-context-menu's ctx-menu.css */
.ctx {
  position: relative;
}

.ctx-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  display: none;
  float: left;
  min-width: 160px;
  padding: 5px 0;
  margin: 2px 0 0;
  font-size: 0.9rem;
  color: #373a3c;
  text-align: left;
  list-style: none;
  background-color: #fff;
  -webkit-background-clip: padding-box;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  -moz-box-shadow: 0 0 5px #ccc;
  -webkit-box-shadow: 0 0 5px #ccc;
  box-shadow: 0 0 5px #ccc;
}

.ctx-divider {
  height: 1px;
  margin: 0.5rem 0;
  overflow: hidden;
  background-color: #e5e5e5;
}

.ctx-item {
  display: block;
  padding: 3px 20px;
  clear: both;
  font-weight: normal;
  line-height: 1.5;
  color: #373a3c;
  text-align: inherit;
  white-space: nowrap;
  background: none;
  border: 0;
  cursor: default;
}

.ctx-item:focus,
.ctx-item:hover {
  color: #2b2d2f;
  text-decoration: none;
  background-color: #f5f5f5;
  cursor: normal;
}

.ctx-item.active,
.ctx-item.active:focus,
.ctx-item.active:hover {
  color: #fff;
  text-decoration: none;
  background-color: #0275d8;
  outline: 0;
}

.ctx-item.disabled,
.ctx-item.disabled:focus,
.ctx-item.disabled:hover {
  color: #818a91;
}

.ctx-item.disabled:focus,
.ctx-item.disabled:hover {
  text-decoration: none;
  cursor: not-allowed;
  background-color: transparent;
  background-image: none;
}

.open > .ctx-menu {
  display: block;
}

.open > a {
  outline: 0;
}

.ctx-menu-right {
  right: 0;
  left: auto;
}

.ctx-menu-left {
  right: auto;
  left: 0;
}

.ctx-header {
  display: block;
  padding: 3px 20px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #818a91;
  white-space: nowrap;
}

.ctx-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 990;
}

.pull-right > .ctx-menu {
  right: 0;
  left: auto;
}

.ctx-menu-container {
  position: fixed;
  padding: 0;
  border: 1px solid #bbb;
  background-color: whitesmoke;
  z-index: 99999;
  box-shadow:
    0 5px 11px 0 rgba(0, 0, 0, 0.18),
    0 4px 15px 0 rgba(0, 0, 0, 0.15);
}
</style>
